import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { useGoogleMapsContext, Util, MediaQuery, InfoWindow } from "@grandvisionhq/common";
import { LocationSearchMap } from "@grandvisionhq/locations";
import { useIntl, useClickAndReserveFeatureConfig } from "@grandvisionhq/state";
import { storesLocatorResultEvent } from "../../../../data-layer/data-layer/actions.js";
import StoreFinderItemContainer from "../../../StoreFinderItem/components/StoreFinderItem/StoreFinderItemContainer.js";
import CurrentLocationPin from "./CurrentLocationPin.js";
import PreferredStorePin from "./PreferredStorePin.js";
/* empty css                             */
import { useStoreFinderContext } from "../../../../context/StoreFinder/context/StoreFinder/context.js";
import { hasStock } from "../../../../utils/utils/has-stock.js";
import { isEqualStores } from "../../../../utils/utils/store-utils.js";
const currentLocationPin = CurrentLocationPin({
  theme: { borderColor: "#2B2E31", background: "#fff" }
});
const preferredStorePinFallback = PreferredStorePin();
const StoreFinderMapContainer = ({
  stores,
  infoWindowContentComponent: InfoWindowContentComponent,
  buttonsComponent: ButtonsComponent,
  showAttributesAsTags
}) => {
  const {
    hoveredStore: hoveredStoreInList,
    config,
    isUsingUserLocation,
    userLocation,
    isUserLocationRequested,
    setIsUserLocationRequested,
    events,
    preferredStore,
    setActiveMapInfoWindow,
    activeMapInfoWindow
  } = useStoreFinderContext();
  const {
    googleMapsSettings: { preferredStorePin }
  } = useGoogleMapsContext();
  const { getLabel } = useIntl();
  const { googleMapsId } = config;
  const [activeStore, setActiveStore] = React.useState(null);
  const clickAndReserveMode = useClickAndReserveFeatureConfig();
  const prevStores = Util.usePrevious(stores);
  const isPhone = MediaQuery.useIsPhone();
  const [hoveredStore, setHoveredStore] = React.useState(null);
  const handleOnMarkerHover = (markerId) => {
    setHoveredStore(markerId);
  };
  const handleOnClickUseMyLocation = React.useCallback(() => {
    if (!isUserLocationRequested) {
      setIsUserLocationRequested(true);
    }
    if (clickAndReserveMode) {
      events?.onClickLocationEvent?.(clickAndReserveMode, "map");
    }
  }, [isUserLocationRequested, events, clickAndReserveMode, setIsUserLocationRequested]);
  const handleOnMarkerClick = React.useCallback(
    (markerId) => {
      if (activeMapInfoWindow === markerId) {
        setActiveMapInfoWindow(null);
        setActiveStore(null);
        return;
      }
      const storesWithPreferredStore = preferredStore ? [...stores, preferredStore] : stores;
      const store = storesWithPreferredStore.find((store2) => store2.globalStoreId === markerId);
      if (store) {
        const dataLayerEvent = storesLocatorResultEvent({
          store,
          preferredStore: preferredStore?.globalStoreId === store.globalStoreId,
          interaction: "map click - marker",
          searchResults: stores.length
        });
        DataLayer.pushEvent(dataLayerEvent);
        setActiveMapInfoWindow(markerId);
        setActiveStore(store);
      }
    },
    [activeMapInfoWindow, setActiveMapInfoWindow, preferredStore, stores]
  );
  const closeInfoWindow = React.useCallback(() => {
    setActiveMapInfoWindow(null);
    setActiveStore(null);
  }, [setActiveMapInfoWindow, setActiveStore]);
  const InfoWindowContent = InfoWindowContentComponent ?? StoreFinderItemContainer;
  const preferredStoreMarkerConfig = React.useCallback(
    (preferredStore2, excludedFromMapBehavior = true) => {
      const isInactive = !!preferredStore2?.stock && !hasStock(preferredStore2.stock);
      return {
        markerId: preferredStore2.globalStoreId,
        title: getLabel("stores.component.storeFinderMap.map.pin.title"),
        position: { lat: Number(preferredStore2.lat), lng: Number(preferredStore2.lon) },
        pin: preferredStorePin || preferredStorePinFallback,
        inactive: isInactive,
        infoWindow: activeMapInfoWindow === preferredStore2.globalStoreId && !isPhone && /* @__PURE__ */ React.createElement(
          InfoWindow,
          {
            position: { lat: Number(preferredStore2.lat), lng: Number(preferredStore2.lon) },
            options: { pixelOffset: { width: 0, height: -4 } },
            onCloseClick: closeInfoWindow
          },
          /* @__PURE__ */ React.createElement(
            InfoWindowContent,
            {
              className: "store-finder-map__info-window-contents",
              store: preferredStore2,
              view: "map",
              buttons: ButtonsComponent ? /* @__PURE__ */ React.createElement(ButtonsComponent, { store: preferredStore2, view: "map" }) : null,
              isPreferredStore: true,
              showAttributesAsTags
            }
          )
        ),
        excludedFromMapBehavior
      };
    },
    [
      ButtonsComponent,
      InfoWindowContent,
      activeMapInfoWindow,
      closeInfoWindow,
      getLabel,
      isPhone,
      preferredStorePin,
      showAttributesAsTags
    ]
  );
  const storeMarkers = React.useMemo(() => {
    return stores.map((store) => {
      const { globalStoreId, lat, lon } = store;
      const isInactive = !!store?.stock && !hasStock(store.stock);
      const storeIsPreferredStore = store.globalStoreId === preferredStore?.globalStoreId;
      if (storeIsPreferredStore) {
        return preferredStoreMarkerConfig(store, false);
      }
      return {
        markerId: globalStoreId,
        title: getLabel("stores.component.storeFinderMap.map.pin.title"),
        position: {
          lat: Number(lat),
          lng: Number(lon)
        },
        inactive: isInactive,
        infoWindow: activeMapInfoWindow === globalStoreId && !isPhone && /* @__PURE__ */ React.createElement(
          InfoWindow,
          {
            position: { lat: Number(lat), lng: Number(lon) },
            options: { pixelOffset: { width: 0, height: -4 } },
            onCloseClick: closeInfoWindow
          },
          /* @__PURE__ */ React.createElement(
            InfoWindowContent,
            {
              className: "store-finder-map__info-window-contents",
              store,
              view: "map",
              buttons: ButtonsComponent ? /* @__PURE__ */ React.createElement(ButtonsComponent, { store, view: "map" }) : null,
              showAttributesAsTags
            }
          )
        )
      };
    });
  }, [
    ButtonsComponent,
    InfoWindowContent,
    activeMapInfoWindow,
    closeInfoWindow,
    getLabel,
    isPhone,
    preferredStore?.globalStoreId,
    preferredStoreMarkerConfig,
    stores,
    showAttributesAsTags
  ]);
  const userLocationMarker = React.useMemo(() => {
    if (isUsingUserLocation && userLocation) {
      return [
        {
          position: { lat: userLocation.lat, lng: userLocation.lng },
          pin: currentLocationPin
        }
      ];
    }
    return [];
  }, [isUsingUserLocation, userLocation]);
  const preferredStoreMarker = React.useMemo(() => {
    const preferredStoreFoundInList = !!stores.filter(
      (store) => store.globalStoreId === preferredStore?.globalStoreId
    ).length;
    if (preferredStore && !preferredStoreFoundInList) {
      return [preferredStoreMarkerConfig(preferredStore)];
    }
    return [];
  }, [preferredStore, preferredStoreMarkerConfig, stores]);
  const markers = React.useMemo(() => {
    return [...storeMarkers, ...userLocationMarker, ...preferredStoreMarker];
  }, [preferredStoreMarker, storeMarkers, userLocationMarker]);
  const center = React.useMemo(() => {
    if (isUsingUserLocation && userLocation !== void 0) {
      return userLocation;
    }
    return void 0;
  }, [userLocation, isUsingUserLocation]);
  React.useEffect(() => {
    if (prevStores && !isEqualStores(prevStores, stores)) {
      setActiveStore(null);
      setActiveMapInfoWindow(null);
    }
  }, [stores, prevStores, setActiveMapInfoWindow]);
  return /* @__PURE__ */ React.createElement(
    LocationSearchMap,
    {
      id: googleMapsId,
      markers,
      markersUpdateConfig: {
        centerToFirst: true,
        zoom: 13
      },
      center,
      highlightMarkerId: activeStore?.globalStoreId ?? null,
      hovered: hoveredStore ?? hoveredStoreInList?.globalStoreId,
      userLocation: isUsingUserLocation && userLocation ? userLocation : void 0,
      userLocationZoomLevel: 13,
      centerOnUserLocation: true,
      activeLocation: isPhone && activeStore ? /* @__PURE__ */ React.createElement("div", { className: "store-finder-map__active-location" }, activeStore ? /* @__PURE__ */ React.createElement(
        InfoWindowContent,
        {
          className: "store-finder-map__active-location-contents",
          store: activeStore,
          buttons: ButtonsComponent ? /* @__PURE__ */ React.createElement(ButtonsComponent, { store: activeStore, view: "map" }) : null,
          view: "map",
          showAttributesAsTags
        }
      ) : null) : null,
      onClickUseMyLocation: handleOnClickUseMyLocation,
      onMarkerClick: handleOnMarkerClick,
      onMarkerHover: handleOnMarkerHover,
      onMapClick: closeInfoWindow
    }
  );
};
export {
  StoreFinderMapContainer as default
};
//# sourceMappingURL=StoreFinderMapContainer.js.map
